"use client";

import { useServiceRating } from "@/hooks/useServiceRating";
import { RatingDisplay } from "./rating/RatingDisplay";
import { HandledDisplay } from "./handled/HandledDisplay";
import WorkerAvatar from "@/app/components/WorkerAvatar";
import { AeraButton } from "@/app/components/ui/AeraButton";

/** แสดงป้าย Beta Free เฉพาะ service นี้ */
const BETA_FREE_SERVICE_ID = "cmjcrj5ad000gg8xz8icco4jq";

// Worker Type
export interface Worker {
  id: string;
  name: string;
  role: string;
  archetype: string;
  rating: number;
  hires: number;
  retainer: number;
  wage: number;
  imageColor: string;
  skills: string[];
  tone: string;
  description: string;
  completedPurchasesCount?: number;
  creatorId?: string;
  thumbnail?: string;
  orderId?: string;
  orderStatus?: string;
  workspaceId?: string;
}

// WorkerCard Component
export const WorkerCard = ({
  worker,
  isSelected,
  onClick,
  isHired,
}: {
  worker: Worker;
  isSelected: boolean;
  onClick: (worker: Worker) => void;
  isHired: boolean;
}) => {
  // Use custom hook for rating data
  const { rating: actualRating, loading: loadingRating } = useServiceRating(
    worker.creatorId,
    worker.id,
  );
  const showBetaFreeBadge = !isHired && worker.id === BETA_FREE_SERVICE_ID;

  // Removed the "wait for image to load before showing anything" logic.
  // This allows the card text to show immediately while the avatar loads progressively via the optimized WorkerAvatar.

  return (
    <AeraButton
      fullWidth
      paddingSize="sm"
      onClick={() => onClick(worker)}
      className={`min-h-16 rounded-xl transition-all! duration-200 text-left group border-0! ${
        isSelected
          ? isHired
            ? "bg-aera-warmcream hover:bg-aera-warmcream"
            : "bg-aera-offwhite hover:bg-aera-offwhite"
          : `bg-transparent ${isHired ? "hover:bg-aera-warmcream" : "hover:bg-aera-white"}`
      }`}
    >
      <div
        className={`relative shrink-0 ${isHired ? "border border-aera-greendark rounded-full p-0.5" : ""}`}
      >
        <WorkerAvatar
          serviceId={worker.id}
          name={worker.name}
          thumbnail={worker.thumbnail}
          size={isHired ? 34 : 40}
          className={`rounded-full`}
          imageColor={worker.imageColor}
        />
        {isHired && (
          <div className="absolute -bottom-[3px] -right-[3px] w-3 h-3 bg-aera-amberdark border border-aera-white rounded-full"></div>
        )}
      </div>
      <div className="block opacity-100 max-w-full flex-1 min-w-0">
        <div
          className={`grid ${showBetaFreeBadge ? "grid-cols-[auto_56px]" : "grid-cols-1"} gap-2 justify-between items-center mb-0 min-w-[150px]`}
        >
          <h3
            className={`font-bold text-sm truncate ${isSelected ? "text-aera-charcoal" : "text-aera-charcoal"}`}
          >
            {worker.name}
          </h3>

          {showBetaFreeBadge && (
            <div className="uppercase text-center text-[9px] font-semibold text-aera-charcoal bg-aera-amber border shadow border-aera-amberlight px-0.5 py-0.5 rounded-full">
              ทดลองฟรี
            </div>
          )}
        </div>
        <p className="text-[10px] text-aera-gray font-medium truncate">
          {isHired ? "ทำงานอยู่" : worker.role}
        </p>

        {!isHired && (
          <div className="flex flex-wrap items-center gap-1">
            <>
              <HandledDisplay count={worker.completedPurchasesCount} />
              {loadingRating ? (
                <span className="text-[10px] text-aera-gray">
                  <div className="animate-spin inline-block w-2 h-2 border border-aera-lightgray/80 border-t-transparent rounded-full"></div>
                </span>
              ) : (
                <RatingDisplay
                  rating={actualRating?.average ?? worker.rating ?? 0}
                />
              )}
            </>
          </div>
        )}
      </div>
    </AeraButton>
  );
};

export default WorkerCard;
